// src/core/CanvasChart.js
// Base class for all canvas-based charts.  
// Adds **optional resizable behaviour**: pass `{ resizable: true }` when you
// instantiate a chart (or set `options.resizable = true` in a subclass).  
// When resizable, grab any edge or corner to resize â€“ the chart redraws live.


class CanvasChart {
  /**
   * @param {HTMLElement|string} container  DOM node or selector
   * @param {Object} [options]
   * @param {boolean} [options.resizable=false]  set true to allow drag-resizing
   * @param {number}  [options.width]   fixed CSS-pixel width  (ignored if resizable=false and container provides width)
   * @param {number}  [options.height]  fixed CSS-pixel height (same note as width)
   */
  constructor(container, options = {}) {
    this.options = options;
    this.pixelRatio = window.devicePixelRatio || 1;

    this.container =
      typeof container === "string"
        ? document.querySelector(container)
        : container;
    if (!this.container) throw new Error("Container element not found");

    // --- Inject an inner wrapper div ---
    // If not already present, create and use it
    let inner = this.container.querySelector('.canvaschart-inner');
    if (!inner) {
      inner = document.createElement('div');
      inner.className = 'canvaschart-inner';
      // Remove container padding from affecting chart area:
      inner.style.position = 'absolute';
      inner.style.top = 0;
      inner.style.left = 0;
      inner.style.right = 0;
      inner.style.bottom = 0;
      inner.style.padding = '0';
      inner.style.margin = '0';
      inner.style.overflow = 'hidden';
      // Make container relative if needed
      if (getComputedStyle(this.container).position === "static") {
        this.container.style.position = "relative";
      }
      this.container.appendChild(inner);
    }
    this.inner = inner;

    // --- Create canvas and append to inner wrapper ---
    this.canvas = document.createElement("canvas");
    this.inner.appendChild(this.canvas);
    this.ctx = this.canvas.getContext("2d");

    this._syncCanvasSize();

    if (options.resizable) this._enableResizeHandles();

    if (typeof ResizeObserver !== "undefined") {
      this._resizeObserver = new ResizeObserver(() => {
        this._syncCanvasSize();
        if (typeof this.render === "function") this.render();
      });
      this._resizeObserver.observe(this.inner); // Use the inner wrapper
    }
  }

  _syncCanvasSize() {
    const dpr = this.pixelRatio;

    // Use inner wrapper's size for the canvas (fills content box)
    let width = this.options.width ?? this.inner.clientWidth;
    let height = this.options.height ?? this.inner.clientHeight;

    if (!width) width = 600;
    if (!height) height = 300;

    this.canvas.style.width = width + "px";
    this.canvas.style.height = height + "px";

    const bufW = Math.round(width * dpr);
    const bufH = Math.round(height * dpr);
    if (this.canvas.width !== bufW || this.canvas.height !== bufH) {
      this.canvas.width = bufW;
      this.canvas.height = bufH;
    }

    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.scale(dpr, dpr);

    return { width, height };
  }

  _enableResizeHandles() {
    const directions = ["n","s","e","w","nw","ne","sw","se",];

    //const directions = ["e", "s", "se"];
    directions.forEach((dir) => {
      const h = document.createElement("div");
      h.className = "resize-handle " + dir;
      h.dataset.dir = dir;
      this.container.appendChild(h);
      h.addEventListener("mousedown", (e) => this._startResize(e, dir));
    });
  }

  _startResize(e, dir) {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = this.container.clientWidth;
    const startH = this.container.clientHeight;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;

      let newW = startW;
      let newH = startH;

      if (dir.includes("e")) newW = startW + dx;
      if (dir.includes("s")) newH = startH + dy;
      if (dir.includes("w")) newW = startW - dx;
      if (dir.includes("n")) newH = startH - dy;

      const minW = this.options.minWidth || 150;
      const minH = this.options.minHeight || 100;
      newW = Math.max(minW, newW);
      newH = Math.max(minH, newH);

      // apply
      this.options.width = undefined; // disable fixed sizes once user resizes
      this.options.height = undefined;
      this.container.style.width = newW + "px";
      this.container.style.height = newH + "px";
      this._syncCanvasSize();
      if (typeof this.render === "function") this.render();
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  /**
   * Replace data & immediately redraw.
   * @param {any} data user-supplied data structure (depends on subclass)
   */
  setData(data) {
    this.data = data;
    if (typeof this.render === "function") this.render();
  }

  clear() {
    const { width, height } = this._syncCanvasSize();
    this.ctx.clearRect(0, 0, width, height);
  }

  /** Destroy chart â€“ remove canvas and any handles */
  destroy() {
    this.container.removeChild(this.canvas);
    if (this.options.resizable) {
      this.container.querySelectorAll(".resize-handle").forEach((h) => h.remove());
      this.container.classList.remove("chart-resizable");
    }
    
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }
}

class Legend {
    /**
     * @param {CanvasRenderingContext2D} ctx
     * @param {{label:string,color:string}[]} items
     * @param {"horizontal"|"vertical"} orientation
     * @param {{x:number,y:number}} anchor  left-top anchor
     */
    static draw(ctx, items, orientation, anchor) {
      const PAD = 12;
      const SW  = 12;              // swatch size
      ctx.font  = "12px sans-serif";
      ctx.textBaseline = "middle";
      ctx.textAlign    = "left";
  
      if (orientation === "horizontal") {
        let lx = anchor.x;
        items.forEach(it => {
          ctx.fillStyle = it.color;
          ctx.fillRect(lx, anchor.y - SW/2, SW, SW);
          ctx.fillStyle = "#ccc";
          ctx.fillText(it.label, lx + SW + 6, anchor.y);
          lx += SW + 6 + ctx.measureText(it.label).width + PAD;
        });
      } else {
        let ly = anchor.y;
        items.forEach(it => {
          ctx.fillStyle = it.color;
          ctx.fillRect(anchor.x, ly - SW/2, SW, SW);
          ctx.fillStyle = "#ccc";
          ctx.fillText(it.label, anchor.x + SW + 6, ly);
          ly += SW + PAD;
        });
      }
    }
  }

function niceCeil(v){const e=10**Math.floor(Math.log10(v));const f=v/e;return (f<=1?1:f<=2?2:f<=5?5:10)*e;}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€ helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
function truncateLabel(lbl,max=32){
  return lbl.length>max ? lbl.slice(0,max)+"â€¦" : lbl;
}


const LEGEND_W  = 100;   // fixed strip reserved for a vertical legend

function decideTicks(maxVal, roomy){
  let ticks = roomy ? 10 : 5;            // preferred counts
  if (maxVal <= 10){
    ticks = Math.max(1, Math.round(maxVal));
  } else {
    while (ticks > 1 && maxVal % ticks !== 0) ticks--;   // back-off to divisor
  }
  return ticks;
}

class MultiChart extends CanvasChart {
  isHorizontal(){ return false; }
  centerXAxisLabels(){ return false; }
  isLegendHorizontal()   { return true; }
  showAxes()            { return true; }

  chartTitle()          { return this.options?.title || ""; }
  chartSubtitle() {
      return this.options?.subtitle ?? "";      // empty string by default
  }

  numericRange(){
    const all = this.chartData.series.flatMap(s=>s.data);
    return { min:0, max:niceCeil(Math.max(...all)) };
  }

_seriesMeta(series) {
    if (this.chartData.entities && Number.isInteger(series.entity)) {
      return this.chartData.entities[series.entity] || { label: "", color: "#fff" };
    }
    return { label: series.label, color: series.color };
  }
  

  setData(d){ this.chartData=d; this.render(); }

  _dims(){ return this._syncCanvasSize(); }

  _prepLayout(){
    const {width:W,height:H}=this._dims();

    /* --- dynamic left margin to fit longest category label (horizontal charts) --- */
    const ctx=this.ctx;
    ctx.save();
    ctx.font="12px sans-serif";
    let maxLblW=0;
    if(this.isHorizontal() && this.chartData){
      maxLblW = Math.max(
        ...this.chartData.labels.map(l=>ctx.measureText(truncateLabel(l)).width)
      );
    }
    ctx.restore();

    const legWidth = this.isLegendHorizontal() ? 0 : LEGEND_W;

    /* â”€â”€ extra padding so the right-most X-axis label (or value) never clips â”€â”€ */
    let rightPad = 0;
    ctx.save();
    ctx.font = "12px sans-serif";
    if (this.isHorizontal()) {
      /* numeric tick at 100 % (or max value) sits at the far-right */
      const maxValStr = this.numericRange().max.toFixed(0);
      rightPad = Math.ceil(ctx.measureText(maxValStr).width / 2) + 8;
    } else if (this.chartData) {
      /* last category label (e.g. â€œWeek 10â€) */
      const lastLbl = this.chartData.labels.at(-1) ?? "";
      rightPad = Math.ceil(ctx.measureText(lastLbl).width / 2) + 8;
    }
    ctx.restore();

    const margin = {
      top:    40,
      right:  Math.max(16 + legWidth, rightPad + legWidth),
      bottom: this.showAxes() ? 28 : 16,
      // 50 px was the old default â€“ keep it unless labels need more
      left:   this.showAxes() ? Math.max(50, Math.ceil(maxLblW)+12) : 16
    };

    const plotW=W-margin.left-margin.right;
    const plotH=H-margin.top-margin.bottom;

    const {min:yMin,max:yMax}=this.numericRange();
    const yRange=yMax-yMin||1;

    let xScale,yScale,catScale;
    if(this.isHorizontal()){
      xScale=v=>margin.left+((v-yMin)/yRange)*plotW;
      const slotH=plotH/this.chartData.labels.length;
      catScale=i=>margin.top+i*slotH+slotH/2;
      yScale=catScale;
    }else {
      yScale=v=>margin.top+plotH-((v-yMin)/yRange)*plotH;
      const xCnt=this.chartData.labels.length;
      xScale=i=>margin.left+(i/(xCnt-1))*plotW;
    }
    return {W,H,margin,plotW,plotH,xScale,yScale,catScale,yMax,yMin,yRange};
  }

  _drawBase(){
    if(!this.chartData) return null;
    const ctx=this.ctx;
    const L=this._prepLayout();
    const {margin,plotW,plotH,yMax,yRange,xScale,catScale}=L;

    ctx.save();
    ctx.font="12px sans-serif";

    if (this.chartTitle()) {
        ctx.save();
        ctx.font = "bold 16px sans-serif";
        ctx.fillStyle = "#eee";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(this.chartTitle(), margin.left, margin.top - 31);
        ctx.restore();
    }

    const sub = this.chartSubtitle?.() || "";
    if (sub) {
      const legendLeft = this.isLegendHorizontal()
          ? L.W
          : L.W - LEGEND_W + 12;
    
      ctx.save();
      ctx.font         = "12px sans-serif";
      ctx.fillStyle    = "#aaa";
      ctx.textAlign    = "left";
      ctx.textBaseline = "top";
      ctx.fillText(sub, legendLeft, margin.top - 26);
      ctx.restore();
    }

    if (this.showAxes()) {
      if(this.isHorizontal()){
        const T = decideTicks(yMax, plotW > 500);
        ctx.strokeStyle="#222";
        for(let i=0;i<=T;i++){
          const x=margin.left+(plotW/T)*i;
          ctx.beginPath();ctx.moveTo(x,margin.top);ctx.lineTo(x,margin.top+plotH);ctx.stroke();
          ctx.fillStyle="#888";
          ctx.textAlign="center";ctx.textBaseline="top";
          ctx.fillText(((yMax/T)*i).toFixed(0),x,margin.top+plotH+4);
        }
        ctx.textAlign="right";ctx.textBaseline="middle";ctx.fillStyle="#888";
        this.chartData.labels.forEach((lbl,i)=>ctx.fillText(truncateLabel(lbl),margin.left-8,catScale(i)));
      }else {
        ctx.strokeStyle="#222";
        const G = decideTicks(yMax, plotH > 250);
        for(let i=0;i<=G;i++){
          const y=margin.top+(i/G)*plotH;
          ctx.beginPath();ctx.moveTo(margin.left,y);ctx.lineTo(margin.left+plotW,y);ctx.stroke();
          ctx.fillStyle="#888";
          ctx.textAlign="right";ctx.textBaseline="middle";
          ctx.fillText((yMax-(i/G)*yRange).toFixed(0),margin.left-6,y);
        }
        /* category labels (bottom) */
        ctx.textAlign="center";ctx.textBaseline="top";ctx.fillStyle="#888";
        const slotW=plotW/this.chartData.labels.length;
        this.chartData.labels.forEach((lbl,i)=>{
          const x=this.centerXAxisLabels()?margin.left+slotW*(i+0.5):xScale(i);
          ctx.fillText(truncateLabel(lbl),x,margin.top+plotH+8);
        });
      }

      ctx.beginPath();ctx.moveTo(margin.left,margin.top);ctx.lineTo(margin.left,margin.top+plotH);ctx.strokeStyle="#444";ctx.stroke();
    }
    ctx.restore();

    ctx.save();
    ctx.font = "12px sans-serif";
    ctx.textBaseline = "middle";

    /* â”€â”€â”€ Legend (shared) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    const legendItems = this.chartData.entities
      ? this.chartData.entities
      : this.chartData.series.map(s => ({ label: s.label, color: s.color }));

    const SW  = 12; 
    const PAD = 12;

    if (this.isLegendHorizontal()) {
      ctx.font = "12px sans-serif";
      const totalW = legendItems.reduce(
        (w, it) => w + SW + 6 + ctx.measureText(it.label).width + PAD,
        -12
      );

      const anchorX = L.W - totalW - 10;
      const anchorY = margin.top - 8;

      Legend.draw(ctx, legendItems, "horizontal", { x: anchorX, y: anchorY });

    } else {
      /* ------- vertical (centred right) ------ */
      ctx.font = "12px sans-serif";
      const rowH   = SW + PAD;
      const totalH = legendItems.length * rowH - PAD;

      const anchorX = L.W - LEGEND_W;
      const anchorY = margin.top + (L.plotH - totalH) / 2;

      Legend.draw(ctx, legendItems, "vertical", { x: anchorX, y: anchorY });
    }

    ctx.restore();

    return L;
  }
}

class HoverLayer {
    constructor(chart) {
      this.chart   = chart;
      this.options = chart.options || {};
      this.container = chart.container;
  
      this.canvas = document.createElement("canvas");
      Object.assign(this.canvas.style, {
        position: "absolute",
        top: this.chart.canvas.offsetTop + "px",
        left: this.chart.canvas.offsetLeft + "px",
        pointerEvents: "none",
      });

      this.chart.canvas.parentNode.insertBefore(this.canvas, this.chart.canvas.nextSibling);
      this.ctx = this.canvas.getContext("2d");
  
      chart.canvas.addEventListener("mousemove", (e) => this._onMove(e));
      chart.canvas.addEventListener("mouseleave", () => this._clear());
    }
  
    _syncSize() {
      const dpr = window.devicePixelRatio || 1;
      const w = this.chart.canvas.width / dpr;
      const h = this.chart.canvas.height / dpr;
      if (!w || !h) return;

      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
      this.canvas.style.width = w + "px";
      this.canvas.style.height = h + "px";

      // Position hover canvas exactly over the chart canvas
      this.canvas.style.top = this.chart.canvas.offsetTop + "px";
      this.canvas.style.left = this.chart.canvas.offsetLeft + "px";

      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  
    setLayout(layout) {
      this._layout = layout;
      this._syncSize();
    }
  
    _onMove(ev) {
      if (!this._layout) return;
  
      const rect = this.chart.canvas.getBoundingClientRect();
      const xCur = ev.clientX - rect.left;
      const yCur = ev.clientY - rect.top;
  
      const { xScale, yScale, margin, plotW } = this._layout;
      const labels  = this.chart.chartData.labels;
      const seriesA = this.chart.chartData.series;
      const interpolate = !!this.options.interpolate;
      const snapPx = this.options.snapPixels ?? 8;
  
      // x-domain fraction â†’ index float
      const idxFloat = ((xCur - margin.left) / plotW) * (labels.length - 1);
      if (idxFloat < 0 || idxFloat > labels.length - 1) {
        this._clear();
        return;
      }
  
      const nearestIdx = Math.round(idxFloat);
      const xVertex = xScale(nearestIdx);
      const snapByX = Math.abs(xVertex - xCur) <= snapPx;
  
      let best = null;
  
      seriesA.forEach((s) => {
        if (snapByX) {
          const v = s.data[nearestIdx];
          const x = xVertex;
          const y = yScale(v);
          const distY = Math.abs(y - yCur);
          if (!best || distY < best.distY) best = { x, y, value: v, distY };
        } else if (interpolate) {
          const i0 = Math.floor(idxFloat);
          const i1 = Math.min(i0 + 1, s.data.length - 1);
          const t  = idxFloat - i0;
          const v  = s.data[i0] + (s.data[i1] - s.data[i0]) * t;
          const x  = xScale(idxFloat);
          const y  = yScale(v);
          const distY = Math.abs(y - yCur);
          if (!best || distY < best.distY) best = { x, y, value: v, distY };
        }
      });
  
      if (best) this._drawOverlay(best.x, best.y, best.value);
      else      this._clear();
    }
  
    _drawOverlay(x, y, value) {
      this._syncSize();
      const ctx = this.ctx;
      const { margin, plotW } = this._layout;
  
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  
      ctx.save();
      ctx.strokeStyle = "#aaa";
      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(margin.left, y);
      ctx.lineTo(margin.left + plotW, y);
      ctx.stroke();
  
      ctx.fillStyle = "#fff";
      ctx.font = "12px sans-serif";
      const txt = value.toFixed(0);
      const padX = 6;
      const boxH = 16;
      const txtW = ctx.measureText(txt).width;
      const boxW = txtW + padX * 2;
      ctx.fillRect(margin.left - boxW - 4, y - boxH / 2, boxW, boxH);
      ctx.fillStyle = "#000";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(txt, margin.left - boxW / 2 - 4, y);
  
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  
    _clear() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

class LineChart extends MultiChart {
  constructor(container, options = {}) {
    super(container, options);

    this._hoverLayer = new HoverLayer(this);
  }

  render() {
    if (!this.chartData) return;

    this.clear();
    const layout = this._drawBase();
    if (!layout) return;

    this._layout = layout;
    this._hoverLayer.setLayout(layout);

    const { xScale, yScale } = layout;
    const ctx = this.ctx;

    ctx.lineWidth = 2;
    ctx.lineJoin  = "round";

    this.chartData.series.forEach((s) => {
      const meta = this._seriesMeta(s);
      ctx.strokeStyle = meta.color;

      ctx.beginPath();
      s.data.forEach((v, i) => {
        const x = xScale(i);
        const y = yScale(v);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke();
    });
  }
}

function hexToRgb$1(hex) {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

class AreaChart extends MultiChart {
  constructor(container, options = {}) {
    super(container, options);
    this._hoverLayer = new HoverLayer(this);
  }

  render() {
    if (!this.chartData) return;

    this.clear();
    const layout = this._drawBase();
    if (!layout) return;
    this._layout = layout;
    this._hoverLayer.setLayout(layout);

    const { xScale, yScale } = layout;
    const ctx = this.ctx;

    this.chartData.series.forEach((s) => {
      const meta = this._seriesMeta(s);
      const [r, g, b] = hexToRgb$1(meta.color);
      const grad = ctx.createLinearGradient(0, Math.min(...s.data.map(v => yScale(v))), 0, yScale(0));
      grad.addColorStop(0, `rgba(${r},${g},${b},0.45)`);
      grad.addColorStop(1, `rgba(${r},${g},${b},0.10)`);
      ctx.fillStyle = grad;

      // area fill
      ctx.beginPath();
      s.data.forEach((v, i) => {
        const x = xScale(i);
        const y = yScale(v);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.lineTo(xScale(s.data.length - 1), yScale(0));
      ctx.lineTo(xScale(0), yScale(0));
      ctx.closePath();
      ctx.fill();

      // ridge stroke
      ctx.beginPath();
      s.data.forEach((v, i) => {
        const x = xScale(i);
        const y = yScale(v);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.strokeStyle = meta.color;
      ctx.lineWidth = 2;
      ctx.lineJoin  = "round";
      ctx.stroke();
    });
  }
}

class Bar {
    /**
     * @param {number} x 
     * @param {number} y  
     * @param {number} w
     * @param {number} h
     * @param {string} color
     * @param {number} value
     */
    constructor(x, y, w, h, color, value) {
      Object.assign(this, { x, y, w, h, color, value });
    }
  
    contains(px, py) {
      return px >= this.x && px <= this.x + this.w && py >= this.y && py <= this.y + this.h;
    }
  
    draw(ctx, gradientTopRgb, gradientBottomRgb, alpha = 0.8) {
      const grad = ctx.createLinearGradient(0, this.y, 0, this.y + this.h);
      grad.addColorStop(0, `rgba(${gradientTopRgb.join(',')},${alpha})`);
      grad.addColorStop(1, `rgba(${gradientBottomRgb.join(',')},${alpha})`);
      ctx.fillStyle = grad;
      ctx.fillRect(this.x, this.y, this.w, this.h);
    }
  }

class BarHoverLayer {
    constructor(chart) {
      this.chart = chart;
      this.container = chart.container;
  
      this.canvas = document.createElement("canvas");
      Object.assign(this.canvas.style, {
        position: "absolute",
        top: this.chart.canvas.offsetTop + "px",
        left: this.chart.canvas.offsetLeft + "px",
        pointerEvents: "none",
      });

      this.chart.canvas.parentNode.insertBefore(this.canvas, this.chart.canvas.nextSibling);
      this.ctx = this.canvas.getContext("2d");
  
      chart.canvas.addEventListener("mousemove", (e) => this._onMove(e));
      chart.canvas.addEventListener("mouseleave", () => this._clear());
    }
  
    setBars(bars, layout) {
      this._bars = bars;
      this._layout = layout;
      this._syncSize();
    }
  
    _syncSize() {
  const dpr = window.devicePixelRatio || 1;
  const w = this.chart.canvas.width / dpr;
  const h = this.chart.canvas.height / dpr;
  if (!w || !h) return;

  this.canvas.width = w * dpr;
  this.canvas.height = h * dpr;
  this.canvas.style.width = w + "px";
  this.canvas.style.height = h + "px";

  // Position hover canvas exactly over the chart canvas
  this.canvas.style.top = this.chart.canvas.offsetTop + "px";
  this.canvas.style.left = this.chart.canvas.offsetLeft + "px";

  this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  
    _onMove(e) {
      if (!this._bars) return;
  
      const rect = this.chart.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
  
      const bar = this._bars.find((b) => b.contains(x, y));
      if (bar) {
        this._drawHover(bar);
      } else {
        this._clear();
      }
    }
  
    _drawHover(bar) {
      this._syncSize();
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.strokeRect(bar.x - 1, bar.y - 1, bar.w + 2, bar.h + 2);
      ctx.restore();
  
      const txt = bar.value.toFixed(0);
      ctx.font = "12px sans-serif";
      const pad = 6;
      const txtW = ctx.measureText(txt).width;
      const boxW = txtW + pad * 2;
      const boxH = 18;
      const boxX = bar.x + bar.w / 2 - boxW / 2;
      const boxY = bar.y - boxH - 4;
  
      ctx.fillStyle = "#fff";
      ctx.fillRect(boxX, boxY, boxW, boxH);
  
      ctx.fillStyle = "#000";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(txt, boxX + boxW / 2, boxY + boxH / 2);
    }
  
    _clear() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

const INNER_RATIO = 0.6;
const GAP_RATIO = 0.10;

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function lerp(a, b, t) { return Math.round(a + (b - a) * t); }
function tint(rgb, frac) { return rgb.map((c) => lerp(c, frac > 0 ? 255 : 0, Math.abs(frac))); }

class BarChart extends MultiChart {
  centerXAxisLabels() { return true; }

  constructor(container, options = {}) {
    super(container, options);
    this._hoverLayer = new BarHoverLayer(this);
  }

  render() {
    if (!this.chartData) return;

    this.clear();
    const L = this._drawBase();
    if (!L) return;
    const { yScale, margin, plotW } = L;

    const ctx = this.ctx;
    const groups = this.chartData.labels.length;
    const seriesArr = this.chartData.series;
    const barsPerGroup = seriesArr.length;

    const groupW = plotW / groups;
    const totalBarZone = groupW * INNER_RATIO;
    const barW = totalBarZone / barsPerGroup;
    const gap = barW * GAP_RATIO;
    const drawW = barW - gap;
    const groupOffset = (groupW - totalBarZone) / 2;

    const bars = [];

    seriesArr.forEach((s, sIdx) => {
      const meta = this._seriesMeta(s);
      const baseRgb = hexToRgb(meta.color);
      const topRgb  = tint(baseRgb, 0.15);
      const bottomRgb = tint(baseRgb, -0.1);

      s.data.forEach((v, i) => {
        const x = margin.left + i * groupW + groupOffset + sIdx * barW + gap / 2;
        const y = yScale(v);
        const h = yScale(0) - y;

        // draw bar
        const grad = ctx.createLinearGradient(0, y, 0, y + h);
        grad.addColorStop(0, `rgba(${topRgb.join(',')},0.8)`);
        grad.addColorStop(1, `rgba(${bottomRgb.join(',')},0.8)`);
        ctx.fillStyle = grad;
        ctx.fillRect(x, y, drawW, h);

        bars.push(new Bar(x, y, drawW, h, meta.color, v));
      });
    });

    this._hoverLayer.setBars(bars, L);
  }
}

function hex2rgb(h){const n=parseInt(h.slice(1),16);return [(n>>16)&255,(n>>8)&255,n&255];}

class StackedBarChart extends MultiChart{
  isHorizontal(){return true;}

  numericRange() {
    const totals = this.chartData.labels.map((_, i) =>
        this.chartData.series.reduce((sum, ser) => sum + (ser.data[i] ?? 0), 0)
    );
    const maxTotal = Math.max(...totals);
    const niceCeil = (v) => {
        const e = 10 ** Math.floor(Math.log10(v));
        const f = v / e;
        return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 3 ? 3 : f <= 4 ? 4 : f <= 5 ? 5 : 10) * e;
    };
    const padded = maxTotal * 1.10;
    return { min: 0, max: niceCeil(padded) };
  }

  constructor(el,opt={}){
    super(el,opt);
    this._hover=new BarHoverLayer(this);
  }

  render(){
    if(!this.chartData)return;
    this.clear();
    const L=this._drawBase();
    if(!L)return;

    const {margin,plotW,catScale,xScale}=L;
    const ctx=this.ctx;
    const rows=this.chartData.labels.length;
    const slotH=plotW? (L.plotH/rows):24;
    const barH=Math.min(24,slotH*0.9);

    const bars=[];
    this.chartData.labels.forEach((_,row)=>{
      let acc=0;
      const y=catScale(row)-barH/2;
      this.chartData.series.forEach(s=>{
        const val=s.data[row]??0;
        const x0=xScale(acc);
        const x1=xScale(acc+val);
        const w  =x1-x0;
        const [r,g,b]=hex2rgb(this._seriesMeta(s).color);
        const grad=ctx.createLinearGradient(x0,0,x1,0);
        grad.addColorStop(0,`rgba(${r},${g},${b},0.8)`);
        grad.addColorStop(1,`rgba(${r*0.8|0},${g*0.8|0},${b*0.8|0},0.8)`);
        ctx.fillStyle=grad;ctx.fillRect(x0,y,w,barH);

        bars.push(new Bar(x0,y,w,barH,`#${((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1)}`,val));
        acc+=val;
      });
    });
    this._hover.setBars(bars,L);
  }
}

const BORDER_W$1 = 2;     // constant black gap
const RING_W   = 4;     // grey inner ring
const LIFT_PX$1  = 8;     // hover lift

function formatValueSmart$1(arg) 
{
  let num = Number(arg); // Make sure it's a real number
  if (!isFinite(num)) return arg; // Or "" if you prefer
  if (Math.abs(num) >= 100) 
  {
    return Math.round(num).toString();
  } 
  else
  {
   return Number(num.toPrecision(3)).toString();
  }
}


class PieSlice {
  constructor(cx, cy, radius, start, end, color, value, percent) {
    Object.assign(this, { cx, cy, radius, start, end, color, value, percent });
  }

  contains(px, py) {
    const dx = px - this.cx, dy = py - this.cy;
    const dist = Math.hypot(dx, dy);
    if (dist > this.radius + BORDER_W$1) return false;

    const TAU = Math.PI * 2;
    let ang = Math.atan2(dy, dx);
    if (ang < 0) ang += TAU;

    let a0 = (this.start < 0) ? this.start + TAU : this.start;
    let a1 = (this.end   < 0) ? this.end   + TAU : this.end;
    if (a1 < a0) a1 += TAU;
    if (ang < a0) ang += TAU;

    return ang >= a0 && ang <= a1;
  }

  draw(ctx, hovered = false) {
    const lift = hovered ? LIFT_PX$1 : 0;
    const mid  = (this.start + this.end) / 2;
    const dx   = Math.cos(mid) * lift;
    const dy   = Math.sin(mid) * lift;
    const cx   = this.cx + dx;
    const cy   = this.cy + dy;

    /* flat fill */
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, this.radius, this.start, this.end);
    ctx.closePath();
    ctx.fillStyle = this.color;
    ctx.fill();

    /* black separator gap */
    ctx.lineWidth = BORDER_W$1;
    ctx.strokeStyle = "#000";
    ctx.stroke();

    /* inner grey ring */
    ctx.beginPath();
    ctx.arc(cx, cy, this.radius - RING_W - 2 - RING_W / 2, this.start, this.end);
    ctx.lineWidth   = RING_W;
    ctx.strokeStyle = "rgba(73, 73, 73, 0.6)";
    ctx.stroke();

    /* % label (inside the slice) */
    ctx.fillStyle   = "#fff";
    ctx.font        = "bold 12px sans-serif";
    ctx.textAlign   = "center";
    ctx.textBaseline= "middle";
    const lx = cx + Math.cos(mid) * (this.radius - BORDER_W$1 - RING_W) * 0.55;
    const ly = cy + Math.sin(mid) * (this.radius - BORDER_W$1 - RING_W) * 0.55;
    const labelValue = formatValueSmart$1(this.percent);
    ctx.fillText(labelValue, lx, ly);
  }
}

// src/charts/PieChart.js
// ----------------------------------------------------------------

const BORDER_W = 2;
const LIFT_PX  = 8;

function formatValueSmart(arg) 
{
  let num = Number(arg); // Make sure it's a real number
  if (!isFinite(num)) return arg; // Or "" if you prefer
  if (Math.abs(num) >= 100) 
  {
    return Math.round(num).toString();
  } 
  else
  {
   return Number(num.toPrecision(3)).toString();
  }
}


class PieChart extends MultiChart {
  constructor(el, opt = {}) {
    super(el, opt);
    this._rowIndex     = opt.rowIndex ?? 0;
    this.hoveredIndex  = -1;
    this.valueType     = opt.valueType === "absolute" ? "absolute" : "percent";

    this.canvas.addEventListener("mousemove", this._onMove.bind(this));
    this.canvas.addEventListener("mouseleave", () => {
      this.hoveredIndex = -1;
      this.render();
    });
  }

  get sequenceIndex()           { return this._rowIndex; }
  set sequenceIndex(idx = 0)    { this._rowIndex = idx; this.render(); }
  
  /** convenient method for chaining */
  setSequenceIndex(idx = 0)     { this.sequenceIndex = idx; return this; }

  /* ---------- subtitle hook ---------- */
  chartSubtitle() {
    if (this.options?.hideSubtitle) return "";
    return this.chartData?.labels?.[this._rowIndex] ?? "";
  }

  showAxes()           { return false; }
  isLegendHorizontal() { return false; }
  numericRange()       { return { min: 0, max: 1 }; }

  /* pointer */
  _onMove(ev) {
    if (!this._slices) return;
    const r = this.canvas.getBoundingClientRect();
    const x = ev.clientX - r.left;
    const y = ev.clientY - r.top;

    const idx = this._slices.findIndex(s => s.contains(x, y));
    if (idx !== this.hoveredIndex) { this.hoveredIndex = idx; this.render(); }
  }

  render() {
    if (!this.chartData) return;
  
    /* clear + draw title / legend / subtitle */
    this.clear();
    const L = this._drawBase();
    if (!L) return;
  
    const ctx = this.ctx;
    const cx  = L.margin.left + L.plotW / 2;
    const cy  = L.margin.top  + L.plotH / 2;
    const R   = Math.min(L.plotW, L.plotH) * 0.45 - BORDER_W;
  
    /* data for the selected row */
    const values = this.chartData.series.map(s => s.data[this._rowIndex] ?? 0);
    const total  = values.reduce((a, b) => a + b, 0) || 1;
  
    /* build slices -------------------------------------------------- */
    this._slices = [];
    let a0 = -Math.PI / 2;
  
    this.chartData.series.forEach((series, i) => {
      const v     = values[i];
      const frac  = v / total;
      const a1    = a0 + frac * Math.PI * 2;
      const color = this._seriesMeta(series).color;
  
      /* label text inside the slice (percent or absolute) */
      const labelTxt = this.valueType === "absolute"
        ? v.toString()
        : Math.round(frac * 100) + "%";
  
      const slice = new PieSlice(cx, cy, R, a0, a1, color, v, labelTxt);
      this._slices.push(slice);
      a0 = a1;
    });
  
    /* pass 1 â€“ draw all non-hovered slices */
    this._slices.forEach((s, i) => {
      if (i !== this.hoveredIndex) s.draw(ctx);          // plain
    });
  
    /* pass 2 â€“ hovered slice + tooltip */
    if (this.hoveredIndex >= 0) {
      const s   = this._slices[this.hoveredIndex];
      const mid = (s.start + s.end) / 2;
  
      /* lift & outline */
      ctx.save();
      ctx.translate(Math.cos(mid) * LIFT_PX, Math.sin(mid) * LIFT_PX);
      s.draw(ctx, true);                                 // hovered = true
      ctx.restore();
  
      /* tooltip ---------------------------------------------------- */
      const isPercentMode = this.valueType !== "absolute";
      const tooltipText   = isPercentMode ? formatValueSmart(s.value) : formatValueSmart(s.percent) + "%";

      ctx.font = "12px sans-serif";
      const pad = 6;
      const tw  = ctx.measureText(tooltipText).width;
      const bw  = tw + pad * 2;

      /* anchor just outside the pie edge */
      let bx = cx + Math.cos(mid) * (R + 26);
      const by = cy + Math.sin(mid) * (R + 26);

      /* clamp so tooltip never leaves canvas horizontally */
      const cw = ctx.canvas.width / ctx.getTransform().a;
      bx = Math.max(bw / 2, Math.min(bx, cw - bw / 2));

      ctx.fillStyle = "#fff";
      ctx.fillRect(bx - bw / 2, by - 10, bw, 20);

      ctx.fillStyle = "#000";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(tooltipText, bx, by);
    }
  }  
}

/*  Tachometer.js
    â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    Draws a semicircular â€œtachometerâ€ gauge that can handle *either* relative
   -segment data (entitiesâ€™ percentages sum â‰ˆ 100) **or** absolute/cumulative
    data (each entityâ€™s percent is an absolute value where the final entity
    reaches 100 â€” e.g. 30 â†’ 69.33 â†’ 100).  The component now auto-detects
    which style is supplied and converts on-the-fly so the gauge renders
    correctly regardless of the input.
*/

/* small helper for floating-point comparisons                                           */
const ALMOST_EQ = (a, b, eps = 1e-2) => Math.abs(a - b) < eps;

class Tachometer extends CanvasChart {
  constructor(el, opt = {}) {
    super(el, opt);
    this.title      = opt.title      ?? "Tachometer";
    this.showLegend = opt.showLegend ?? false;
    this.value      = 0;             // current indicator position (0-100)
  }

  /* public API ---------------------------------------------------------------------- */
  setValue(v) { this.value = Math.max(0, Math.min(100, v)); this.render(); }
  setData(d)  { this.data  = d; this.render(); }

  /* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
  /** Convert `entities` so each item has a `.segPercent` that is the *segment width*
      (i.e. fraction of the gauge the entity should occupy).  Works for:
        â€¢ relative input (percents already sum to 100)
        â€¢ absolute/cumulative input (percents monotonic â†‘, last == 100)                */
  _deriveSegments() {
    if (!this.data?.entities?.length) return [];

    const ents = this.data.entities;
    const pctSum = ents.reduce((s, e) => s + e.percent, 0);
    const lastIs100 = ALMOST_EQ(ents[ents.length - 1].percent, 100);

    /* Heuristic: if they *donâ€™t* sum to 100 *and* the last entry is 100,
       we assume the list is cumulative/absolute.                                      */
    const isAbsolute = !ALMOST_EQ(pctSum, 100) && lastIs100;

    if (!isAbsolute) {
      /* already relative â€“ assign as-is */
      return ents.map(e => ({ ...e, segPercent: e.percent }));
    }

    /* convert cumulative â†’ relative */
    let prev = 0;
    return ents.map(e => {
      const width = Math.max(0, e.percent - prev);   // guard against bad data
      prev = e.percent;
      return { ...e, segPercent: width };
    });
  }

  /* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
  render() {
    super.clear();

    const { width: W, height: H } = this._syncCanvasSize();
    const ctx = this.ctx;

    /* -----  Heading ---------------------------------------------------------------- */
    ctx.fillStyle   = "#fff";
    ctx.font        = "bold 16px sans-serif";
    ctx.textAlign   = "left";
    ctx.textBaseline= "top";
    ctx.fillText(this.title, 10, 12);

    if (!this.data) {
      ctx.fillText("No data", 10, 28);
      return;
    }

    /* â”€â”€ dynamic margin so annotation boxes always fit â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    const ANN_OFFSET = 32;              // distance label boxes sit outside rim
    ctx.save();
    ctx.font = "12px sans-serif";
    const padBox = 5;
    const maxAnnWidth = this.data
      ? Math.max(
          ...this.data.annotations.map(a => ctx.measureText(a.label).width + 2 * padBox)
        )
      : 0;
    ctx.restore();

    const GAUGE_MARGIN = Math.max(40, Math.ceil(maxAnnWidth / 2 + ANN_OFFSET));
    const size = Math.min(W, H) - GAUGE_MARGIN * 2;   // gauge diameter
    const cx   = W / 2;
    const cy   = H / 2 + size * 0.05;
    const RO   = size / 2;          // outer radius
    const RI   = RO - 20;           // inner radius for ticks

    /* -----  Drawing constants ------------------------------------------------------- */
    const START = Math.PI * 0.75;   // left-hand start angle
    const ARC   = Math.PI * 1.5;    // total sweep (270Â°)
    const GAP   = Math.PI / 90;     // small gap between coloured arcs

    /* -----  Convert entity data â†’ drawable segments -------------------------------- */
    const segments = this._deriveSegments();
    if (!segments.length) return;

    /* -----  Coloured arcs ----------------------------------------------------------- */
    ctx.lineWidth = 8;
    let ang = START;

    segments.forEach((seg, idx) => {
      const sweepFull = (seg.segPercent / 100) * ARC;
      const sweep     = idx === segments.length - 1 ? sweepFull
                                                    : sweepFull - GAP;

      ctx.beginPath();
      ctx.arc(cx, cy, RO, ang, ang + sweep);
      ctx.strokeStyle = seg.color;
      ctx.stroke();

      ang += sweep + GAP;
    });

    /* -----  Radial ticks ------------------------------------------------------------ */
    const TICKS = 60, T_LEN = 16;
    ctx.lineWidth = 3;
    for (let i = 0; i <= TICKS; i++) {
      const frac = i / TICKS;
      const a    = START + frac * ARC;
      const cos  = Math.cos(a), sin = Math.sin(a);

      ctx.beginPath();
      ctx.moveTo(cx + cos * (RI - T_LEN), cy + sin * (RI - T_LEN));
      ctx.lineTo(cx + cos * RI,           cy + sin * RI);
      ctx.strokeStyle = frac <= this.value / 100 ? "#00aaff" : "#5d5d5d";
      ctx.stroke();
    }

    /* -----  Annotation labels ------------------------------------------------------- */
    ctx.font = "12px sans-serif";
    const ANN_TICK = 12;

    this.data.annotations.forEach(ann => {
      const frac = ann.percent / 100;
      const a    = START + frac * ARC;
      const cos  = Math.cos(a), sin = Math.sin(a);

      /* small radial tick for annotation */
      ctx.lineWidth = 4;
      ctx.strokeStyle = "#fff";
      ctx.beginPath();
      ctx.moveTo(cx + cos * (RI - T_LEN - 4), cy + sin * (RI - T_LEN - 4));
      ctx.lineTo(cx + cos * (RI + ANN_TICK),   cy + sin * (RI + ANN_TICK));
      ctx.stroke();

      /* label background box */
      const txt = ann.label;
      const pad = 5;
      const tw  = ctx.measureText(txt).width;
      const bx  = cx + cos * (RO + ANN_OFFSET) - tw / 2 - pad;
      const by  = cy + sin * (RO + ANN_OFFSET) - 10;

      ctx.lineWidth   = 1;
      ctx.strokeStyle = "rgba(255,255,255,0.5)";
      ctx.strokeRect(bx, by, tw + pad * 2, 18);

      /* label text */
      ctx.fillStyle   = "#fff";
      ctx.textAlign   = "center";
      ctx.textBaseline= "middle";
      ctx.fillText(txt, bx + (tw + pad * 2) / 2, by + 10);
    });

    /* -----  Central numeric read-out ----------------------------------------------- */
    const bigFont  = Math.round(size * 0.26);
    const valueStr = this.value.toFixed(0);

    ctx.save();
    ctx.translate(cx, cy - 4);
    ctx.scale(0.88, 1);
    ctx.font         = `500 ${bigFont}px sans-serif`;
    ctx.fillStyle    = "#fff";
    ctx.textAlign    = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(valueStr, 0, 0);
    /* scaled width of numeric text (measureText is un-scaled â†’ Ã—0.88) */
    const numWidth = ctx.measureText(valueStr).width * 0.88;
    ctx.restore();

    ctx.font         = `${Math.round(bigFont * 0.42)}px sans-serif`;
    ctx.fillStyle    = "rgba(255,255,255,0.8)";
    ctx.textAlign    = "left";
    ctx.textBaseline = "middle";
    /* place â€œ%â€ 6 px to the right of the numeric textâ€™s edge               */
    ctx.fillText("%", cx + numWidth / 2, cy - bigFont * 0.22);

    /* -----  Optional legend --------------------------------------------------------- */
    if (this.showLegend) {
      Legend.draw(ctx, this.data.entities, "vertical",
                  { x: W - 100 + 12, y: 20 });
    }
  }
}

export { AreaChart, BarChart, LineChart, PieChart, StackedBarChart, Tachometer };